import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Global 402 (Payment Required / quota-exceeded) handler: surfaces a friendly
// upgrade modal anywhere in the app. Components' own catch blocks still run
// because we re-reject the error.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const resp = error?.response;
    if (resp?.status === 402) {
      const detail = (resp.data && resp.data.detail) || {};
      const payload = (detail && typeof detail === 'object') ? detail : { message: detail };
      try {
        window.dispatchEvent(new CustomEvent('quota:exceeded', { detail: payload }));
      } catch (_) { /* noop */ }
    }
    // Normalize an object `detail` into a plain string message so no component can
    // ever try to render an object as a React child ("Objects are not valid as a
    // React child"). The original object is preserved on `detailObj` if needed.
    try {
      const d = resp && resp.data && resp.data.detail;
      if (d && typeof d === 'object') {
        resp.data.detailObj = d;
        resp.data.detail = d.message || d.error || 'Request failed';
      }
    } catch (_) { /* noop */ }
    return Promise.reject(error);
  }
);

// Client-side cache for S3 pre-signed URLs. They are valid for 8h; we reuse a
// cached URL for up to 7h (safety margin) so repeat previews/downloads are instant
// instead of round-tripping to the backend every time.
const _presignedCache = new Map(); // docId -> { res, ts }
const PRESIGNED_TTL_MS = 7 * 60 * 60 * 1000;

const api = {
  // Auth
  login: (data) => axios.post(`${API_URL}/api/auth/login`, data),
  register: (data) => axios.post(`${API_URL}/api/auth/register`, data),

  // Documents
  listDocuments: (params, headers) => axios.get(`${API_URL}/api/documents/`, { params, headers }),
  getDocument: (id, headers) => axios.get(`${API_URL}/api/documents/${id}`, { headers }),
  uploadDocument: (formData, headers) => axios.post(`${API_URL}/api/documents/`, formData, { headers }),
  updateDocument: (id, data, headers) => {
    const formData = new FormData();
    if (data.title !== undefined) formData.append('title', data.title);
    if (data.description !== undefined) formData.append('description', data.description || '');
    if (data.doc_type !== undefined) formData.append('doc_type', data.doc_type);
    if (data.tags !== undefined) formData.append('tags', JSON.stringify(data.tags));
    return axios.put(`${API_URL}/api/documents/${id}`, formData, { headers });
  },
  deleteDocument: (id, headers) => axios.delete(`${API_URL}/api/documents/${id}`, { headers }),
  downloadDocument: (id, headers) => axios.get(`${API_URL}/api/documents/${id}/download`, { headers, responseType: 'blob' }),
  checkoutDocument: (id, headers) => axios.post(`${API_URL}/api/documents/${id}/checkout`, {}, { headers }),
  checkinDocument: (id, formData, headers) => axios.post(`${API_URL}/api/documents/${id}/checkin`, formData, { headers }),
  cancelCheckout: (id, headers) => axios.post(`${API_URL}/api/documents/${id}/cancel-checkout`, {}, { headers }),
  getDocumentVersions: (id, headers) => axios.get(`${API_URL}/api/documents/${id}/versions`, { headers }),
  getDocumentPreviewUrl: (id, headers) => axios.get(`${API_URL}/api/documents/${id}/preview-url`, { headers }),
  getDocumentPresignedUrl: (id, headers, expiresIn = 28800) => {
    const cached = _presignedCache.get(id);
    if (cached && Date.now() - cached.ts < PRESIGNED_TTL_MS) {
      return Promise.resolve(cached.res);
    }
    return axios
      .get(`${API_URL}/api/documents/${id}/presigned-url`, {
        headers,
        params: { expires_in: expiresIn },
      })
      .then((res) => {
        // Only cache real pre-signed (https) URLs, not local-stream fallbacks.
        if (res?.data?.url?.startsWith('http')) {
          _presignedCache.set(id, { res, ts: Date.now() });
        }
        return res;
      });
  },
  invalidatePresignedCache: (id) => {
    if (id) _presignedCache.delete(id); else _presignedCache.clear();
  },
  getDocumentPreview: (id, headers) => axios.get(`${API_URL}/api/documents/${id}/preview`, { headers, responseType: 'blob' }),
  indexDocument: (id, headers) => axios.post(`${API_URL}/api/documents/${id}/index`, {}, { headers }),
  indexAllDocuments: (headers) => axios.post(`${API_URL}/api/documents/index-all`, {}, { headers }),

  // Search
  searchDocuments: (params, headers) => axios.get(`${API_URL}/api/search/documents`, { params, headers }),

  // Workflows
  getMyTasks: (headers) => axios.get(`${API_URL}/api/workflows/instances/my-tasks`, { headers }),
  getMyTasksDetailed: (headers) => axios.get(`${API_URL}/api/workflows/instances/my-tasks/detailed`, { headers }),
  getWorkflowByDocument: (docId, headers) => axios.get(`${API_URL}/api/workflows/instances/by-document/${docId}`, { headers }),
  getWorkflowInsights: (headers) => axios.get(`${API_URL}/api/workflows/insights`, { headers }),
  cleanupLegacyInstances: (dryRun, headers) => axios.post(`${API_URL}/api/workflows/instances/cleanup-legacy?dry_run=${dryRun}`, {}, { headers }),
  listWorkflows: (headers) => axios.get(`${API_URL}/api/workflows/`, { headers }),
  getWorkflowDefinitions: (headers) => axios.get(`${API_URL}/api/workflows/`, { headers }),
  getWorkflow: (workflowId, headers) => axios.get(`${API_URL}/api/workflows/${workflowId}`, { headers }),
  createWorkflow: (data, headers) => axios.post(`${API_URL}/api/workflows/`, data, { headers }),
  updateWorkflow: (workflowId, data, headers) => axios.put(`${API_URL}/api/workflows/${workflowId}`, data, { headers }),
  createWorkflowDefinition: (data, headers) => axios.post(`${API_URL}/api/workflows/definitions`, data, { headers }),
  startWorkflow: (docId, workflowId, headers) => 
    axios.post(`${API_URL}/api/workflows/instances/start?doc_id=${docId}&workflow_id=${workflowId}`, {}, { headers }),
  performWorkflowAction: (instanceId, action, comments, headers) => 
    axios.post(`${API_URL}/api/workflows/instances/${instanceId}/action`, { action, comments }, { headers }),

  // System Health
  getSystemHealth: (headers) => axios.get(`${API_URL}/api/system-health/`, { headers }),
  getHealthTimeseries: (days, headers) => axios.get(`${API_URL}/api/system-health/timeseries?days=${days}`, { headers }),
  getTenantBreakdown: (headers) => axios.get(`${API_URL}/api/system-health/tenants`, { headers }),
  getProcessingPerformance: (headers) => axios.get(`${API_URL}/api/system-health/performance`, { headers }),

  // Accuracy & Quality
  getAccuracyDashboard: (headers) => axios.get(`${API_URL}/api/accuracy/dashboard`, { headers }),
  getAccuracyCorrections: (params, headers) => axios.get(`${API_URL}/api/accuracy/corrections`, { params, headers }),

  // Numeric Validation
  getNumericValidation: (docId, headers) => axios.get(`${API_URL}/api/documents/${docId}/numeric-validation`, { headers }),

  // Annotations
  listAnnotations: (docId, headers) => axios.get(`${API_URL}/api/annotations/${docId}`, { headers }),
  createAnnotation: (docId, data, headers) => axios.post(`${API_URL}/api/annotations/${docId}`, data, { headers }),
  updateAnnotation: (docId, annId, data, headers) => axios.put(`${API_URL}/api/annotations/${docId}/${annId}`, data, { headers }),
  deleteAnnotation: (docId, annId, headers) => axios.delete(`${API_URL}/api/annotations/${docId}/${annId}`, { headers }),

  // Chat
  askQuestion: (data, headers) => axios.post(`${API_URL}/api/chat/ask`, data, { headers }),

  // Companies
  listCompanies: (headers) => axios.get(`${API_URL}/api/companies/`, { headers }),
  createCompany: (data, headers) => axios.post(`${API_URL}/api/companies/`, data, { headers }),

  // Users
  listUsers: (headers) => axios.get(`${API_URL}/api/users/`, { headers }),
  createUser: (data, headers) => axios.post(`${API_URL}/api/users/`, data, { headers }),
  updateUser: (id, data, headers) => axios.put(`${API_URL}/api/users/${id}`, data, { headers }),

  // Audit
  getAuditLogs: (params, headers) => axios.get(`${API_URL}/api/audit/`, { params, headers }),

  // Virtual Views
  getVirtualFolderSuggestions: (headers) => axios.get(`${API_URL}/api/virtual-views/suggestions`, { headers }),
  getSmartRecommendations: (headers) => axios.get(`${API_URL}/api/virtual-views/smart-recommendations`, { headers }),

  // Compliance
  checkDisposition: (headers) => axios.get(`${API_URL}/api/compliance/disposition/check`, { headers }),
  applyDisposition: (data, headers) => axios.post(`${API_URL}/api/compliance/disposition/apply`, data, { headers }),
  getRetentionSummary: (headers) => axios.get(`${API_URL}/api/compliance/retention/summary`, { headers }),

  // Metadata Suggestions
  getMetadataSuggestions: (docId, headers) => axios.get(`${API_URL}/api/documents/${docId}/metadata-suggestions`, { headers }),
  applyMetadata: (docId, formData, headers) => axios.post(`${API_URL}/api/documents/${docId}/apply-metadata`, formData, { headers }),

  // ==================== PHASE 2 & 3 ADVANCED FEATURES ====================

  // Security & Anomaly Detection
  getSecurityIncidents: (params, headers) => axios.get(`${API_URL}/api/advanced/security/incidents`, { params, headers }),
  getSecurityAlerts: (headers) => axios.get(`${API_URL}/api/advanced/security/alerts`, { headers }),
  unfreezeUser: (userId, formData, headers) => axios.post(`${API_URL}/api/advanced/security/unfreeze/${userId}`, formData, { headers }),
  resolveIncident: (incidentId, formData, headers) => axios.post(`${API_URL}/api/advanced/security/incidents/${incidentId}/resolve`, formData, { headers }),

  // Contract Intelligence
  analyzeContract: (docId, headers) => axios.post(`${API_URL}/api/advanced/contracts/${docId}/analyze`, {}, { headers }),
  getContractObligations: (params, headers) => axios.get(`${API_URL}/api/advanced/contracts/obligations`, { params, headers }),
  getExpiringContracts: (days, headers) => axios.get(`${API_URL}/api/advanced/contracts/expiring`, { params: { days }, headers }),
  compareContractVersions: (docId, formData, headers) => axios.post(`${API_URL}/api/advanced/contracts/${docId}/compare`, formData, { headers }),

  // Natural Language Workflows
  parseWorkflowDescription: (formData, headers) => axios.post(`${API_URL}/api/advanced/workflows/parse`, formData, { headers }),
  getWorkflowDrafts: (params, headers) => axios.get(`${API_URL}/api/advanced/workflows/drafts`, { params, headers }),
  previewWorkflowDraft: (draftId, headers) => axios.get(`${API_URL}/api/advanced/workflows/drafts/${draftId}/preview`, { headers }),
  activateWorkflowDraft: (draftId, headers) => axios.post(`${API_URL}/api/advanced/workflows/drafts/${draftId}/activate`, {}, { headers }),

  // AI Agents
  queryPolicyAgent: (formData, headers) => axios.post(`${API_URL}/api/advanced/agents/policy/query`, formData, { headers }),
  queryAuditAgent: (formData, headers) => axios.post(`${API_URL}/api/advanced/agents/audit/query`, formData, { headers }),
  getComplianceReport: (reportType, headers) => axios.get(`${API_URL}/api/advanced/agents/audit/compliance-report`, { params: { report_type: reportType }, headers }),
  queryVendorAgent: (formData, headers) => axios.post(`${API_URL}/api/advanced/agents/vendor/query`, formData, { headers }),
  getVendorSummary: (headers) => axios.get(`${API_URL}/api/advanced/agents/vendor/summary`, { headers }),

  // Auto-Redaction
  analyzeForRedaction: (docId, headers) => axios.post(`${API_URL}/api/advanced/redaction/${docId}/analyze`, {}, { headers }),
  createRedactedCopy: (docId, formData, headers) => axios.post(`${API_URL}/api/advanced/redaction/${docId}/create-copy`, formData, { headers }),
  getRedactionHistory: (params, headers) => axios.get(`${API_URL}/api/advanced/redaction/history`, { params, headers }),

  // Knowledge Relationships
  analyzeDocumentCorpus: (headers) => axios.post(`${API_URL}/api/advanced/knowledge/analyze-corpus`, {}, { headers }),
  getRelatedDocuments: (docId, limit, headers) => axios.get(`${API_URL}/api/advanced/knowledge/${docId}/related`, { params: { limit }, headers }),
  getKnowledgeGraph: (params, headers) => axios.get(`${API_URL}/api/advanced/knowledge/graph`, { params, headers }),
  createDocumentRelationship: (formData, headers) => axios.post(`${API_URL}/api/advanced/knowledge/relationships`, formData, { headers }),
  suggestDocumentTags: (docId, headers) => axios.get(`${API_URL}/api/advanced/knowledge/${docId}/suggest-tags`, { headers }),

  // Physical Folders
  listFolders: (headers) => axios.get(`${API_URL}/api/folders/`, { headers }),
  createFolder: (data, headers) => axios.post(`${API_URL}/api/folders/`, data, { headers }),
  renameFolder: (id, data, headers) => axios.put(`${API_URL}/api/folders/${id}`, data, { headers }),
  deleteFolder: (id, headers) => axios.delete(`${API_URL}/api/folders/${id}`, { headers }),
  getFolderDocuments: (id, headers) => axios.get(`${API_URL}/api/folders/${id}/documents`, { headers }),
  moveDocument: (docId, folderId, headers) => axios.put(`${API_URL}/api/documents/${docId}/move`, { folder_id: folderId }, { headers }),

  // Bulk document operations
  bulkUploadDocuments: (formData, headers, onProgress) => axios.post(`${API_URL}/api/documents/bulk-upload`, formData, { headers, onUploadProgress: onProgress }),
  bulkDownloadDocuments: (docIds, headers) => axios.post(`${API_URL}/api/documents/bulk-download`, { doc_ids: docIds }, { headers, responseType: 'blob' }),

  // Sharing
  createShareLink: (docId, data, headers) => axios.post(`${API_URL}/api/sharing/documents/${docId}/share`, data, { headers }),
  listShareLinks: (docId, headers) => axios.get(`${API_URL}/api/sharing/documents/${docId}/shares`, { headers }),
  revokeShareLink: (shareId, headers) => axios.delete(`${API_URL}/api/sharing/shares/${shareId}`, { headers }),
  accessSharedDocument: (token, password) => axios.get(`${API_URL}/api/sharing/shared/${token}`, { params: password ? { password } : {} }),

  // ==================== PHASE 3 (Block-C) ====================

  // Notifications (P3-A)
  getNotifications: (headers, unreadOnly = true) => axios.get(`${API_URL}/api/notifications/`, { params: { unread_only: unreadOnly }, headers }),
  markNotificationRead: (id, headers) => axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { headers }),
  markAllNotificationsRead: (headers) => axios.put(`${API_URL}/api/notifications/read-all`, {}, { headers }),

  // Document lineage (P3-B)
  getDocumentLineage: (id, headers) => axios.get(`${API_URL}/api/documents/${id}/lineage`, { headers }),

  // Executive Command Center (P3-C)
  getCommandCenter: (headers) => axios.get(`${API_URL}/api/executive/command-center`, { headers }),
  getAIGovernance: (headers) => axios.get(`${API_URL}/api/executive/ai-governance`, { headers }),

  // Benchmarking + Heatmaps (P3-D / P3-F)
  getDepartmentPerformance: (headers) => axios.get(`${API_URL}/api/benchmarking/department-performance`, { headers }),
  getApprovalEfficiency: (headers) => axios.get(`${API_URL}/api/benchmarking/approval-efficiency`, { headers }),
  getVendorPerformance: (headers) => axios.get(`${API_URL}/api/benchmarking/vendor-performance`, { headers }),
  getDeptRiskHeatmap: (headers) => axios.get(`${API_URL}/api/benchmarking/heatmaps/department-risk`, { headers }),
  getVendorRiskHeatmap: (headers) => axios.get(`${API_URL}/api/benchmarking/heatmaps/vendor-risk`, { headers }),
  getWorkflowRiskHeatmap: (headers) => axios.get(`${API_URL}/api/benchmarking/heatmaps/workflow-risk`, { headers }),

  // Governance reports (P3-E)
  listGovernanceReports: (headers) => axios.get(`${API_URL}/api/governance/reports/`, { headers }),
  generateGovernanceReport: (period, headers) => {
    const fd = new FormData();
    fd.append('period', period);
    return axios.post(`${API_URL}/api/governance/reports/generate`, fd, { headers });
  },
  downloadGovernanceReport: (id, headers) => axios.get(`${API_URL}/api/governance/reports/${id}/download`, { headers, responseType: 'blob' }),

  // Meeting Prep (P3-G)
  prepareMeetingBrief: (formData, headers) => axios.post(`${API_URL}/api/meeting-prep/prepare`, formData, { headers, responseType: 'blob' }),
  prepareMeetingBriefJSON: (formData, headers) => axios.post(`${API_URL}/api/meeting-prep/prepare`, formData, { headers }),
  getRecentBriefs: (headers) => axios.get(`${API_URL}/api/meeting-prep/recent`, { headers }),
  getKnownVendors: (headers) => axios.get(`${API_URL}/api/meeting-prep/vendors`, { headers }),

  // ===== Revenue & Subscription Engine (Block-G) =====
  // Plans
  revListPlans: (headers) => axios.get(`${API_URL}/api/revenue/plans`, { headers }),
  revCreatePlan: (data, headers) => axios.post(`${API_URL}/api/revenue/plans`, data, { headers }),
  revUpdatePlan: (id, data, headers) => axios.put(`${API_URL}/api/revenue/plans/${id}`, data, { headers }),
  revDeletePlan: (id, headers) => axios.delete(`${API_URL}/api/revenue/plans/${id}`, { headers }),
  // Feature matrix
  revListFeatures: (headers) => axios.get(`${API_URL}/api/revenue/feature-matrix`, { headers }),
  revUpsertFeature: (data, headers) => axios.post(`${API_URL}/api/revenue/feature-matrix`, data, { headers }),
  revDeleteFeature: (id, headers) => axios.delete(`${API_URL}/api/revenue/feature-matrix/${id}`, { headers }),
  // AI actions (pricing master)
  revListAIActions: (headers) => axios.get(`${API_URL}/api/revenue/ai-actions`, { headers }),
  revUpdateAIAction: (id, data, headers) => axios.put(`${API_URL}/api/revenue/ai-actions/${id}`, data, { headers }),
  revCreateAIAction: (data, headers) => axios.post(`${API_URL}/api/revenue/ai-actions`, data, { headers }),
  // OCR rates
  revListOCRRates: (headers) => axios.get(`${API_URL}/api/revenue/ocr/rates`, { headers }),
  revUpsertOCRRate: (data, headers) => axios.post(`${API_URL}/api/revenue/ocr/rates`, data, { headers }),
  // Wallets
  revAIWallet: (headers, tenantId) => axios.get(`${API_URL}/api/revenue/ai-credits/wallet`, { headers, params: tenantId ? { tenant_id: tenantId } : {} }),
  revAITransactions: (headers) => axios.get(`${API_URL}/api/revenue/ai-credits/transactions`, { headers }),
  revAllocateCredits: (data, headers) => axios.post(`${API_URL}/api/revenue/ai-credits/allocate`, data, { headers }),
  revCreditCheck: (data, headers) => axios.post(`${API_URL}/api/revenue/ai-credits/check`, data, { headers }),
  revCreditConsume: (data, headers) => axios.post(`${API_URL}/api/revenue/ai-credits/consume`, data, { headers }),
  // Subscriptions
  revListSubscriptions: (headers) => axios.get(`${API_URL}/api/revenue/subscriptions`, { headers }),
  revMySubscription: (headers) => axios.get(`${API_URL}/api/revenue/subscriptions/me`, { headers }),
  revAssignSubscription: (data, headers) => axios.post(`${API_URL}/api/revenue/subscriptions`, data, { headers }),
  // Discounts
  revListDiscounts: (headers) => axios.get(`${API_URL}/api/revenue/discounts`, { headers }),
  revCreateDiscount: (data, headers) => axios.post(`${API_URL}/api/revenue/discounts`, data, { headers }),
  revDeleteDiscount: (id, headers) => axios.delete(`${API_URL}/api/revenue/discounts/${id}`, { headers }),
  // Invoices
  revListInvoices: (headers, tenantId) => axios.get(`${API_URL}/api/revenue/invoices`, { headers, params: tenantId ? { tenant_id: tenantId } : {} }),
  revCreateInvoice: (data, headers) => axios.post(`${API_URL}/api/revenue/invoices`, data, { headers }),
  revInvoicePdf: (id, headers) => axios.get(`${API_URL}/api/revenue/invoices/${id}/pdf`, { headers, responseType: 'blob' }),
  // Audit + config + dashboard
  revAuditLogs: (headers) => axios.get(`${API_URL}/api/revenue/audit-logs`, { headers }),
  revGetConfig: (headers) => axios.get(`${API_URL}/api/revenue/config`, { headers }),
  revSetEnforcement: (data, headers) => axios.put(`${API_URL}/api/revenue/config/enforcement-mode`, data, { headers }),
  revSetGST: (data, headers) => axios.put(`${API_URL}/api/revenue/config/gst`, data, { headers }),
  revDashboard: (headers) => axios.get(`${API_URL}/api/revenue/dashboard/summary`, { headers }),
  revQuotaHits: (days, headers) => axios.get(`${API_URL}/api/revenue/quota-hits/summary?days=${days || 30}`, { headers }),
  // Billing / Stripe
  revPackages: (headers) => axios.get(`${API_URL}/api/revenue/billing/packages`, { headers }),
  revCheckout: (data, headers) => axios.post(`${API_URL}/api/revenue/billing/stripe/checkout`, data, { headers }),
  revCheckoutStatus: (sessionId, headers) => axios.get(`${API_URL}/api/revenue/billing/stripe/status/${sessionId}`, { headers }),
  revRazorpayCheckout: (data, headers) => axios.post(`${API_URL}/api/revenue/billing/razorpay/checkout`, data, { headers }),
  revRazorpayConfirm: (orderId, headers) => axios.post(`${API_URL}/api/revenue/billing/razorpay/confirm/${orderId}`, {}, { headers }),
  revRazorpayVerify: (data, headers) => axios.post(`${API_URL}/api/revenue/billing/razorpay/verify`, data, { headers }),
  getTenantLimits: (tenantId, headers) => axios.get(`${API_URL}/api/revenue/tenants/${tenantId}/limits`, { headers }),
  setTenantLimits: (tenantId, data, headers) => axios.put(`${API_URL}/api/revenue/tenants/${tenantId}/limits`, data, { headers }),
  revPaymentHistory: (headers) => axios.get(`${API_URL}/api/revenue/billing/transactions`, { headers }),
  revReceiptPdf: (txnId, headers) => axios.get(`${API_URL}/api/revenue/billing/transactions/${txnId}/receipt`, { headers, responseType: 'blob' }),

  // ===== Revenue v3 spec-named methods =====
  getPlans: (h) => axios.get(`${API_URL}/api/revenue/plans`, { headers: h }),
  createPlan: (d, h) => axios.post(`${API_URL}/api/revenue/plans`, d, { headers: h }),
  updatePlan: (id, d, h) => axios.put(`${API_URL}/api/revenue/plans/${id}`, d, { headers: h }),
  clonePlan: (id, h) => axios.post(`${API_URL}/api/revenue/plans/${id}/clone`, {}, { headers: h }),
  togglePlan: (id, action, h) => axios.post(`${API_URL}/api/revenue/plans/${id}/${action}`, {}, { headers: h }),
  deletePlan: (id, h) => axios.delete(`${API_URL}/api/revenue/plans/${id}`, { headers: h }),

  getFeatureMatrix: (h) => axios.get(`${API_URL}/api/revenue/features`, { headers: h }),
  getMyFeatures: (h) => axios.get(`${API_URL}/api/revenue/features/my`, { headers: h }),
  getStorageInfo: (h) => axios.get(`${API_URL}/api/documents/storage/info`, { headers: h }),
  updateFeature: (key, d, h) => axios.put(`${API_URL}/api/revenue/features/${key}`, d, { headers: h }),

  getWallet: (h) => axios.get(`${API_URL}/api/revenue/ai-credits/wallet`, { headers: h }),
  estimateCredit: (key, h) => axios.get(`${API_URL}/api/revenue/ai-credits/estimate?action_key=${key}`, { headers: h }),
  getCreditTransactions: (h) => axios.get(`${API_URL}/api/revenue/ai-credits/transactions`, { headers: h }),
  getActionMaster: (h) => axios.get(`${API_URL}/api/revenue/ai-credits/action-master`, { headers: h }),
  updateActionCost: (key, d, h) => axios.put(`${API_URL}/api/revenue/ai-credits/action-master/${key}`, d, { headers: h }),
  topupCredits: (tid, d, h) => axios.post(`${API_URL}/api/revenue/ai-credits/wallet/${tid}/topup`, d, { headers: h }),

  getOcrRates: (h) => axios.get(`${API_URL}/api/revenue/ocr/rate-master`, { headers: h }),
  updateOcrRate: (type, d, h) => axios.put(`${API_URL}/api/revenue/ocr/rate-master/${type}`, d, { headers: h }),
  getOcrWallet: (h) => axios.get(`${API_URL}/api/revenue/ocr/wallet`, { headers: h }),
  rerunOcr: (docId, h) => axios.post(`${API_URL}/api/documents/${docId}/ocr`, {}, { headers: h }),

  getMySubscription: (h) => axios.get(`${API_URL}/api/revenue/subscriptions/my`, { headers: h }),
  getAllSubscriptions: (h) => axios.get(`${API_URL}/api/revenue/subscriptions`, { headers: h }),
  assignSubscription: (d, h) => axios.post(`${API_URL}/api/revenue/subscriptions`, d, { headers: h }),
  subscriptionAction: (id, action, h) => axios.put(`${API_URL}/api/revenue/subscriptions/${id}/${action}`, {}, { headers: h }),

  getDiscounts: (h) => axios.get(`${API_URL}/api/revenue/discounts`, { headers: h }),
  createDiscount: (d, h) => axios.post(`${API_URL}/api/revenue/discounts`, d, { headers: h }),
  deactivateDiscount: (id, h) => axios.post(`${API_URL}/api/revenue/discounts/${id}/deactivate`, {}, { headers: h }),

  getBilling: (h) => axios.get(`${API_URL}/api/revenue/billing`, { headers: h }),
  generateBilling: (tid, d, h) => axios.post(`${API_URL}/api/revenue/billing/generate/${tid}`, d, { headers: h }),

  getInvoices: (h) => axios.get(`${API_URL}/api/revenue/invoices`, { headers: h }),
  createInvoiceReq: (d, h) => axios.post(`${API_URL}/api/revenue/invoices`, d, { headers: h }),
  bulkGenerateInvoices: (h) => axios.post(`${API_URL}/api/revenue/invoices/bulk-generate`, {}, { headers: h }),
  downloadInvoice: (id, h) => axios.get(`${API_URL}/api/revenue/invoices/${id}/download`, { headers: h, responseType: 'blob' }),
  downloadReceipt: (id, h) => axios.get(`${API_URL}/api/revenue/invoices/${id}/download-receipt`, { headers: h, responseType: 'blob' }),
  markInvoicePaid: (id, d, h) => axios.put(`${API_URL}/api/revenue/invoices/${id}/mark-paid`, d, { headers: h }),
  updateInvoiceIrn: (id, d, h) => axios.put(`${API_URL}/api/revenue/invoices/${id}/update-irn`, d, { headers: h }),

  getRevenueSummary: (h) => axios.get(`${API_URL}/api/revenue/dashboard/summary`, { headers: h }),
  getTenantDashboard: (h) => axios.get(`${API_URL}/api/revenue/tenant-dashboard/summary`, { headers: h }),
  getRevenueSettings: (h) => axios.get(`${API_URL}/api/revenue/settings`, { headers: h }),
  saveRevenueSettings: (d, h) => axios.put(`${API_URL}/api/revenue/settings`, d, { headers: h }),
};

export default api;
